import { useMemo, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Field,
  Input,
  Money,
  PageHeader,
  SkeletonRows,
  StatTile,
  Toolbar,
  addDecimal,
  cmpDecimal,
  cn,
  formatMoney,
  formatPercent,
  isZeroDecimal,
  mulDecimal,
  roundDecimalString,
  subDecimal,
} from '@biovet/ui';
import {
  useResource,
  type BalanceSheetResult,
  type ProfitAndLossResult,
  type StatementRow,
} from '../lib/api.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportRow, type ReportTable } from '../lib/print.js';
import {
  baseCurrency,
  companyToday,
  fiscalYear,
  fiscalYearToDate,
  monthOf,
  shiftMonth,
} from '../lib/reference-data.js';


interface Section {
  title: string;
  groups: string[];
}

const PL_SECTIONS: Section[] = [
  { title: 'Revenue', groups: ['REVENUE'] },
  { title: 'Cost of sales', groups: ['COST_OF_SALES'] },
  { title: 'Operating expenses', groups: ['OPERATING_EXPENSES'] },
  { title: 'Other income', groups: ['OTHER_INCOME'] },
];

const BS_ASSETS: Section[] = [
  {
    title: 'Current assets',
    groups: [
      'CASH',
      'BANK',
      'RECEIVABLES',
      'INVENTORY',
      'ADVANCES',
      'TAX_ASSETS',
      'CURRENT_ASSETS',
    ],
  },
  { title: 'Non-current assets', groups: ['FIXED_ASSETS'] },
];

const BS_FUNDING: Section[] = [
  { title: 'Current liabilities', groups: ['PAYABLES', 'TAX_LIABILITIES', 'CURRENT_LIABILITIES'] },
  { title: 'Non-current liabilities', groups: ['NON_CURRENT_LIABILITIES'] },
  { title: 'Equity', groups: ['EQUITY'] },
];

function total(rows: StatementRow[]): string {
  return rows.reduce((acc, row) => addDecimal(acc, row.amountBase), '0');
}

function inSection(rows: StatementRow[], section: Section): StatementRow[] {
  return rows.filter((row) => row.statementGroup && section.groups.includes(row.statementGroup));
}

function partition(
  rows: StatementRow[],
  sections: Section[],
): Array<{ section: Section; rows: StatementRow[] }> {
  const claimed = new Set(sections.flatMap((s) => s.groups));
  const result = sections.map((section) => ({ section, rows: inSection(rows, section) }));
  const leftovers = rows.filter((row) => !row.statementGroup || !claimed.has(row.statementGroup));

  if (leftovers.length > 0) {
    result.push({
      section: { title: 'Other — unmapped statement group', groups: [] },
      rows: leftovers,
    });
  }
  return result.filter((entry) => entry.rows.length > 0);
}


export function ProfitAndLossScreen() {
  const [range, setRange] = useState(() => fiscalYearToDate());

  const { data, error, loading } = useResource<ProfitAndLossResult>('/profit-and-loss', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const rows = data?.rows ?? [];

  const figures = useMemo(() => {
    const sections = partition(rows, PL_SECTIONS);
    const revenue = total(inSection(rows, PL_SECTIONS[0] as Section));
    const cogs = total(inSection(rows, PL_SECTIONS[1] as Section));
    const opex = total(inSection(rows, PL_SECTIONS[2] as Section));
    const gross = subDecimal(revenue, cogs);
    const net = subDecimal(
      total(rows.filter((r) => r.type === 'INCOME')),
      total(rows.filter((r) => r.type === 'EXPENSE')),
    );
    const marginPct = isZeroDecimal(revenue)
      ? '0'
      : roundDecimalString(mulDecimal(divide(gross, revenue), '100'), 2);
    return { sections, revenue, cogs, opex, gross, net, marginPct };
  }, [rows]);

  const loss = cmpDecimal(figures.net, '0') < 0;

  const report = (): ReportTable => {
    const rowsOut: ReportRow[] = [];
    for (const { section, rows: sectionRows } of figures.sections) {
      rowsOut.push({ cells: [section.title, '', ''], head: true });
      for (const row of sectionRows) {
        rowsOut.push([row.code, row.name, formatMoney(row.amountBase)]);
      }
      rowsOut.push({
        cells: ['', `Total ${section.title.toLowerCase()}`, formatMoney(total(sectionRows))],
        total: true,
      });
      if (section.title === 'Cost of sales') {
        rowsOut.push({ cells: ['', 'Gross profit', formatMoney(figures.gross)], total: true });
      }
    }
    rowsOut.push({
      cells: [
        '',
        loss ? 'Net loss for the period' : 'Net profit for the period',
        formatMoney(figures.net),
      ],
      total: true,
    });

    return {
      title: 'Statement of profit and loss',
      fileName: 'profit-and-loss',
      meta: `For ${data?.from ?? range.from} to ${data?.to ?? range.to} · ${baseCurrency()} · gross margin ${formatPercent(figures.marginPct)} of revenue`,
      columns: [
        { label: 'Code', width: '84px' },
        { label: 'Account' },
        { label: `Amount ${baseCurrency()}`, align: 'right', width: '140px' },
      ],
      rows: rowsOut,
      notes: ['Drafts are excluded — the reporting views read posted lines only.'],
    };
  };

  return (
    <>
      <PageHeader
        breadcrumb="Finance & Accounts / Profit & Loss"
        title="Profit &amp; loss"
        description="What the business earned and spent over the period below."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => printReport(report())}>
              Print
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportReport(report())}>
              Export XLSX
            </Button>
          </>
        }
        meta={
          <>
            <Chip>{fiscalYear().name}</Chip>
            <Chip>PKR</Chip>
            <Badge tone={loss ? 'bad' : 'ok'}>
              {loss ? 'Loss' : 'Profit'} {formatMoney(abs(figures.net))}
            </Badge>
          </>
        }
      />

      {error && (
        <div className="pb-3.5">
          <Callout tone="danger" title="Could not load the profit and loss">
            {error}
          </Callout>
        </div>
      )}

      <Card flush className="mb-3.5" data-noprint="1">
        <Toolbar>
          <Field label="From">
            <Input
              size="sm"
              type="date"
              value={range.from}
              aria-label="From date"
              onChange={(e) => setRange({ ...range, from: e.target.value })}
            />
          </Field>
          <Field label="To">
            <Input
              size="sm"
              type="date"
              value={range.to}
              aria-label="To date"
              onChange={(e) => setRange({ ...range, to: e.target.value })}
            />
          </Field>
          <Button size="sm" variant="ghost" onClick={() => setRange(fiscalYearToDate())}>
            This year
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRange(monthOf(companyToday()))}>
            This month
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setRange(shiftMonth(range.from, -1))}
            title="The month before the one shown"
          >
            Previous month
          </Button>
        </Toolbar>
      </Card>

      <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">
        <Card>
          <StatTile label="Revenue" value={formatMoney(figures.revenue, { decimals: 0 })} />
        </Card>
        <Card>
          <StatTile
            label="Gross profit"
            value={formatMoney(figures.gross, { decimals: 0 })}
            hint={`${formatPercent(figures.marginPct)} of revenue`}
          />
        </Card>
        <Card>
          <StatTile label="Operating expenses" value={formatMoney(figures.opex, { decimals: 0 })} />
        </Card>
        <Card>
          <StatTile
            label={loss ? 'Net loss' : 'Net profit'}
            value={formatMoney(abs(figures.net), { decimals: 0 })}
            hint="before tax"
          />
        </Card>
      </div>

      <Card flush>
        <CardHeader
          title="Statement of profit and loss"
          subtitle={`For ${data?.from ?? range.from} to ${data?.to ?? range.to} · PKR`}
        />
        {loading ? (
          <SkeletonRows rows={10} cols={3} />
        ) : (
          <div className="flex flex-col">
            {figures.sections.map(({ section, rows: sectionRows }) => (
              <div key={section.title} className="flex flex-col">
                <StatementSection section={section} rows={sectionRows} revenue={figures.revenue} />
                {section.title === 'Cost of sales' && (
                  <ResultRow label="Gross profit" value={figures.gross} revenue={figures.revenue} />
                )}
              </div>
            ))}
            <ResultRow
              label={loss ? 'Net loss for the period' : 'Net profit for the period'}
              value={figures.net}
              revenue={figures.revenue}
              emphasis
            />
          </div>
        )}
      </Card>

      <p data-report-foot="1" className="hidden pt-4 text-[11px] text-[var(--ink-3)]">
        System-generated from biovet Accounting. Figures in PKR.
      </p>
    </>
  );
}


export function BalanceSheetScreen() {
  const [asOn, setAsOn] = useState(() => companyToday());

  const sheet = useResource<BalanceSheetResult>('/balance-sheet', { asOn: asOn || undefined });
  const all = sheet.data?.rows ?? [];

  const rows = all.filter((r) => !r.isResult);
  const resultRow = all.find((r) => r.isResult);

  const figures = useMemo(() => {
    const assets = partition(
      rows.filter((r) => r.type === 'ASSET'),
      BS_ASSETS,
    );
    const funding = partition(
      rows.filter((r) => r.type === 'LIABILITY' || r.type === 'EQUITY'),
      BS_FUNDING,
    );
    const assetTotal = assets.reduce((acc, s) => addDecimal(acc, total(s.rows)), '0');
    const fundingTotal = funding.reduce((acc, s) => addDecimal(acc, total(s.rows)), '0');

    const result = resultRow?.amountBase ?? '0';

    const fundingWithResult = addDecimal(fundingTotal, result);
    return {
      assets,
      funding,
      assetTotal,
      fundingTotal,
      result,
      fundingWithResult,
      difference: subDecimal(assetTotal, fundingWithResult),
    };
  }, [rows, resultRow]);

  const balanced = cmpDecimal(figures.difference, '0') === 0;
  const loading = sheet.loading;
  const error = sheet.error;

  const report = (): ReportTable => {
    const rowsOut: ReportRow[] = [];
    const section = (entries: typeof figures.assets) => {
      for (const { section: s, rows: sectionRows } of entries) {
        rowsOut.push({ cells: [s.title, '', ''], head: true });
        for (const row of sectionRows) {
          rowsOut.push([row.code, row.name, formatMoney(row.amountBase)]);
        }
        rowsOut.push({
          cells: ['', `Total ${s.title.toLowerCase()}`, formatMoney(total(sectionRows))],
          total: true,
        });
      }
    };

    section(figures.assets);
    rowsOut.push({ cells: ['', 'Total assets', formatMoney(figures.assetTotal)], total: true });
    section(figures.funding);
    rowsOut.push(['', 'Result for the period, not yet journalled', formatMoney(figures.result)]);
    rowsOut.push({
      cells: ['', 'Total liabilities and equity', formatMoney(figures.fundingWithResult)],
      total: true,
    });

    return {
      title: 'Balance sheet',
      fileName: 'balance-sheet',
      meta: `As at ${sheet.data?.asOn ?? asOn} · ${baseCurrency()} · ${
        balanced ? 'balanced' : `out by ${formatMoney(figures.difference)}`
      }`,
      columns: [
        { label: 'Code', width: '84px' },
        { label: 'Account' },
        { label: `Amount ${baseCurrency()}`, align: 'right', width: '140px' },
      ],
      rows: rowsOut,
      notes: [
        'The current year’s result is transferred into equity on the face of this statement; the journal that makes it permanent is the year-end close.',
      ],
    };
  };

  return (
    <>
      <PageHeader
        breadcrumb="Finance & Accounts / Balance Sheet"
        title="Balance sheet"
        description="What the business owns and owes as at the date below."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => printReport(report())}>
              Print
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportReport(report())}>
              Export XLSX
            </Button>
          </>
        }
        meta={
          <>
            <Chip>{fiscalYear().name}</Chip>
            <Chip>PKR</Chip>
            {!loading &&
              rows.length > 0 &&
              (balanced ? (
                <Badge tone="ok" dot>
                  Balances
                </Badge>
              ) : (
                <Badge tone="bad">Out by {formatMoney(figures.difference)}</Badge>
              ))}
          </>
        }
      />

      {error && (
        <div className="pb-3.5">
          <Callout tone="danger" title="Could not load the balance sheet">
            {error}
          </Callout>
        </div>
      )}

      <Card flush className="mb-3.5" data-noprint="1">
        <Toolbar>
          <Field label="As at">
            <Input
              size="sm"
              type="date"
              value={asOn}
              aria-label="As at date"
              onChange={(e) => setAsOn(e.target.value)}
            />
          </Field>
          <Button size="sm" variant="ghost" onClick={() => setAsOn(companyToday())}>
            Today
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAsOn(fiscalYear().endDate || companyToday())}
            title="The last day of the open fiscal year"
          >
            Year end
          </Button>
          <div className="flex-1" />
          <span className="text-[11.5px] text-[var(--ink-3)]">
            Everything posted on or before {sheet.data?.asOn ?? asOn}.
          </span>
        </Toolbar>
      </Card>

      <div className="grid gap-3.5 xl:grid-cols-2">
        <Card flush>
          <CardHeader
            title="Assets"
            subtitle={`What the business holds at ${sheet.data?.asOn ?? asOn}`}
          />
          {loading ? (
            <SkeletonRows rows={8} cols={2} />
          ) : (
            <div className="flex flex-col">
              {figures.assets.map(({ section, rows: sectionRows }) => (
                <StatementSection key={section.title} section={section} rows={sectionRows} />
              ))}
              <ResultRow label="Total assets" value={figures.assetTotal} emphasis />
            </div>
          )}
        </Card>

        <Card flush>
          <CardHeader title="Liabilities and equity" subtitle="How it is funded" />
          {loading ? (
            <SkeletonRows rows={8} cols={2} />
          ) : (
            <div className="flex flex-col">
              {figures.funding.map(({ section, rows: sectionRows }) => (
                <StatementSection key={section.title} section={section} rows={sectionRows} />
              ))}
              <Line
                label="Current year result — transferred from profit and loss"
                value={figures.result}
                muted
              />
              <ResultRow
                label="Total liabilities and equity"
                value={figures.fundingWithResult}
                emphasis
              />
            </div>
          )}
        </Card>
      </div>

      {!loading && rows.length > 0 && !balanced && (
        <div className="pt-3.5">
          <Callout tone="danger" title="The balance sheet does not balance">
            Assets exceed liabilities and equity by {formatMoney(figures.difference)}. Every account
            with a balance must map to a statement group — an unmapped one drops off the face of the
            statement without affecting the trial balance, which is the usual cause.
          </Callout>
        </div>
      )}

      <p data-report-foot="1" className="hidden pt-4 text-[11px] text-[var(--ink-3)]">
        System-generated from biovet Accounting. Figures in PKR.
      </p>
    </>
  );
}


function StatementSection({
  section,
  rows,
  revenue,
}: {
  section: Section;
  rows: StatementRow[];
  revenue?: string;
}) {
  if (rows.length === 0) return null;

  return (
    <>
      <div className="bb-eyebrow border-b border-[var(--line-2)] bg-[var(--surface-2)] px-[var(--pad)] py-2">
        {section.title}
      </div>
      {rows.map((row) => (
        <Line
          key={row.code}
          label={
            <>
              <span className="bb-mono pr-2 text-[11.5px] text-[var(--ink-3)]">{row.code}</span>
              {row.name}
            </>
          }
          value={row.amountBase}
          revenue={revenue}
          indent
        />
      ))}
      <Line label={`Total ${section.title.toLowerCase()}`} value={total(rows)} subtotal />
    </>
  );
}

function Line({
  label,
  value,
  revenue,
  indent,
  subtotal,
  muted,
}: {
  label: ReactNode;
  value: string;
  revenue?: string;
  indent?: boolean;
  subtotal?: boolean;
  muted?: boolean;
}) {
  const pct =
    revenue && !isZeroDecimal(revenue)
      ? roundDecimalString(mulDecimal(divide(value, revenue), '100'), 1)
      : null;

  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 border-b border-[var(--line-2)] px-[var(--pad)] py-[7px]',
        subtotal && 'bg-[var(--surface-2)] font-semibold',
        muted && 'text-[var(--ink-2)] italic',
      )}
    >
      <span className={cn('min-w-0 text-[12.5px]', indent && 'pl-4')}>{label}</span>
      <span className="flex shrink-0 items-baseline gap-3">
        {pct && !subtotal && (
          <span className="bb-num w-12 text-right text-[11px] text-[var(--ink-3)]">{pct}%</span>
        )}
        <Money value={value} className="w-[132px] text-right text-[12.5px]" />
      </span>
    </div>
  );
}

function ResultRow({
  label,
  value,
  revenue,
  emphasis,
}: {
  label: string;
  value: string;
  revenue?: string;
  emphasis?: boolean;
}) {
  const pct =
    revenue && !isZeroDecimal(revenue)
      ? roundDecimalString(mulDecimal(divide(value, revenue), '100'), 1)
      : null;

  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 px-[var(--pad)] py-2.5',
        emphasis
          ? 'border-t-2 border-[var(--line)] bg-[var(--surface-2)]'
          : 'border-y border-[var(--line-2)]',
      )}
    >
      <span className="text-[13px] font-semibold">{label}</span>
      <span className="flex shrink-0 items-baseline gap-3">
        {pct && (
          <span className="bb-num w-12 text-right text-[11px] text-[var(--ink-3)]">{pct}%</span>
        )}
        <Money
          value={value}
          colorBySign={cmpDecimal(value, '0') < 0}
          className="w-[132px] text-right text-[13px] font-semibold"
        />
      </span>
    </div>
  );
}


function divide(numerator: string, denominator: string): string {
  if (isZeroDecimal(denominator)) return '0';
  return (Number(numerator) / Number(denominator)).toFixed(6);
}

function abs(value: string): string {
  return value.startsWith('-') ? value.slice(1) : value;
}
